import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { showAlert, showConfirm } from '@platform/ui';
import { BusinessAlert, BusinessConfirm } from '@platform/billing';
import { useAuth } from '@/features/auth';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { businessApi } from '../api/business.api';
import type { Organization } from '../types/business.type';
import { UserType } from '@/shared/constants/auth/auth.constant.ts';

type GateResult = { ok: false } | { ok: true; group: Organization };

/** 서버(axios) 에러의 실제 메시지 추출 — 실패 원인 노출용. */
function extractServerMessage(e: unknown): string | undefined {
  if (typeof e !== 'object' || e === null) return undefined;
  const data = (e as { response?: { data?: { message?: unknown } } }).response
    ?.data?.message;
  if (typeof data === 'string' && data.trim()) return data;
  if (Array.isArray(data) && typeof data[0] === 'string') return data[0];
  return undefined;
}

/**
 * 스토어 **신규 기업 구독** 진입점.
 *
 * 새 계약이 생기는 행위라 사업자 계정(UserType.BUSINESS)을 요구한다.
 *
 * ⚠️ 이미 있는 구독의 **좌석 증감**은 여기 없다 — console-web 의 같은 이름 훅이 맡는다.
 *    그쪽은 계정 유형이 아니라 그 조직에서의 OrgRole(PURCHASE_LICENSE)로 판정하므로
 *    (초대받은 개인 관리자도 통과해야 한다) 판정 축 자체가 다르다.
 */
export function useBusinessPurchase() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  /** 로그인 확인 — 두 게이트 공통 선행 조건. */
  const requireLogin = useCallback(async (): Promise<boolean> => {
    if (user) return true;
    const go = await showConfirm(BusinessConfirm.loginRequired);
    if (go) navigate(PATHS.LOGIN);
    return false;
  }, [user, navigate]);

  /** 조직 검증 상태 분기 — 두 게이트 공통. 통과 시 true. */
  const assertVerified = useCallback(
    async (group: Organization): Promise<boolean> => {
      if (group.verificationStatus === 'PENDING') {
        await showAlert(BusinessAlert.verificationPending);
        navigate(PATHS.BUSINESS_VERIFICATION);
        return false;
      }
      if (group.verificationStatus !== 'VERIFIED') {
        await showAlert(BusinessAlert.verificationRequired);
        navigate(PATHS.BUSINESS_VERIFICATION);
        return false;
      }
      return true;
    },
    [navigate],
  );

  /**
   * 스토어 신규 구독 게이트.
   *  - 비로그인        → 로그인 유도
   *  - 개인(INDIVIDUAL) → 기업 회원가입 안내. **전환은 없다** — 개인 계정은 기업
   *                      계정이 될 수 없으므로 새 계정을 만들어야 한다
   *  - 기업·조직없음    → 관리자 문의 안내. 여기서 조직을 만들지 않는다
   *  - 기업·미검증/심사 → 검증 페이지로 이동
   *  - 기업·검증완료    → 구매 진행 가능 (group 반환)
   */
  const resolveStoreGate = useCallback(async (): Promise<GateResult> => {
    if (!(await requireLogin()) || !user) return { ok: false };

    // 개인 계정 → 기업 회원가입 안내 (신규 계약 주체는 사업자여야 한다).
    // 전환 경로가 없으므로 마이페이지가 아니라 가입 화면으로 보낸다.
    if (user.userType === UserType.INDIVIDUAL) {
      const go = await showConfirm(BusinessConfirm.businessSignupRequired);
      if (go) navigate(PATHS.SIGNUP);
      return { ok: false };
    }

    // 기업 계정 → 소속 조직 확인.
    // 조직이 없으면 여기서 만들지 않는다 — 화면 입력값으로 org 를 만들면 검증되지
    // 않은 사업자번호가 그대로 들어가고, 그 번호의 진짜 사업자가 선점당한다.
    // 조직 생성은 국세청 검증을 통과한 기업회원 확정 시 서버가 프로비저닝한다.
    const memberships = await businessApi.myGroups();
    if (memberships.length === 0) {
      const go = await showConfirm(BusinessConfirm.orgNotProvisioned);
      if (go) navigate(PATHS.MYPAGE);
      return { ok: false };
    }
    const m = memberships[0];
    const group: Organization =
      m.group ?? (await businessApi.getGroup(m.orgId));

    if (!(await assertVerified(group))) return { ok: false };
    return { ok: true, group };
  }, [user, navigate, requireLogin, assertVerified]);

  /**
   * 스토어 구매 게이트 — **구매 Modal·카드등록 전에** 막힐 사유를 모두 확인한다.
   *
   * resolveStoreGate(로그인·회원유형·조직·검증)에 더해 "이미 이 플랜을 구독 중인지"까지 본다.
   * 결제 단위는 플랜이라 같은 플랜은 새로 팔지 않고 콘솔 좌석 증감으로 보낸다. 다른 플랜은 상품이 겹쳐도 산다.
   * 판정·문구는 서버(purchase-blocks)가 구매 거절과 같은 기준으로 내려준다 — 여기서 상태를 따로 고르면 카드등록 뒤에 튕긴다.
   */
  const resolveStorePurchaseGate = useCallback(
    async (
      planIds: string[],
    ): Promise<{ ok: false } | { ok: true; orgId: string }> => {
      const gate = await resolveStoreGate();
      if (!gate.ok) return { ok: false };

      try {
        setSubmitting(true);
        const blocks = await businessApi.orgPurchaseBlocks(gate.group.id);
        const hits = blocks.filter((b) => planIds.includes(b.planId));
        if (hits.length > 0) {
          const go = await showConfirm({
            title: '이미 구독 중인 플랜입니다',
            message:
              `${hits.map((b) => `· ${b.planName} — ${b.message}`).join('\n')}\n\n` +
              '같은 플랜은 새로 구매하지 않고 기존 구독에서 좌석(수량)을 조정합니다. ' +
              '기업 콘솔의 라이선스 관리에서 진행해 주세요.',
            confirmText: '콘솔로 이동',
            cancelText: '닫기',
          });
          if (go) navigate(PATHS.BUSINESS_LICENSES);
          return { ok: false };
        }
        return { ok: true, orgId: gate.group.id };
      } catch (e) {
        console.error('[business purchase] gate failed', e);
        await showAlert({
          ...BusinessAlert.purchaseFailed,
          message:
            extractServerMessage(e) ?? BusinessAlert.purchaseFailed.message,
        });
        return { ok: false };
      } finally {
        setSubmitting(false);
      }
    },
    [resolveStoreGate, navigate],
  );

  return {
    submitting,
    resolveStoreGate,
    resolveStorePurchaseGate,
  };
}
